import type { PlayerBonus, PlayerFreespin } from 'api/common';
import {
  getUserStorageList,
  setUserStorageItems,
  userStorageItems,
} from 'api/userStorage';
import { LocalStorage, LocalStorageKeys } from 'helpers/localStorage';

const BONUSES_LAST_SEEN_AT_IDENTIFIER = 'bonuses_last_seen_at';

interface IBonusesLastSeenAtInfo {
  last_seen_at: string;
}

type StoredBonusesLastSeenAtInfo = Partial<
  IBonusesLastSeenAtInfo & { lastSeenAt: string }
>;

const getLocalStorage = (userId: number) =>
  new LocalStorage<string, null>(
    `${LocalStorageKeys.BonusesLastSeenAt}-${userId}`,
    null
  );

const warnLastSeenAtNotStored = (reason: unknown) =>
  console.warn('bonuses_last_seen_at was not stored on the server', reason);

export const getSeenTimestamp = (
  bonuses: PlayerBonus[],
  freespins: PlayerFreespin[]
) => {
  const issuedTimes = [...bonuses, ...freespins]
    .map(({ createdAt }) => new Date(createdAt).getTime() + 1)
    .filter((time) => !Number.isNaN(time));

  return new Date(Math.max(Date.now(), ...issuedTimes)).toISOString();
};

export const getBonusesLastSeenAt = async (
  userId: number,
  isServerStorageEnabled: boolean
): Promise<string | null> => {
  if (isServerStorageEnabled) {
    try {
      const { data } = await userStorageItems();

      const item = getUserStorageList(data).find(
        ({ identifier }) => identifier === BONUSES_LAST_SEEN_AT_IDENTIFIER
      );

      const info = (item?.info ?? {}) as StoredBonusesLastSeenAtInfo;
      const lastSeenAt = info.lastSeenAt ?? info.last_seen_at;

      if (lastSeenAt) return lastSeenAt;
    } catch (error) {
      warnLastSeenAtNotStored(error);
    }
  }

  return getLocalStorage(userId).get();
};

export const saveBonusesLastSeenAt = async (
  userId: number,
  isServerStorageEnabled: boolean,
  timestamp: string
): Promise<void> => {
  try {
    getLocalStorage(userId).replace(timestamp);
  } catch (error) {
    warnLastSeenAtNotStored(error);
  }

  if (!isServerStorageEnabled) return;

  const info: IBonusesLastSeenAtInfo = { last_seen_at: timestamp };

  try {
    await setUserStorageItems([
      { identifier: BONUSES_LAST_SEEN_AT_IDENTIFIER, info },
    ]);
  } catch (error) {
    warnLastSeenAtNotStored(error);
  }
};
